// Sending one file to the cloud machine, and the uploads running in each session (drops.ts runs them).
//
// An upload whose bytes stop moving ends with why, as the core's does (internal/cli/drops.go,
// watchUpload): on 2026-10-15 the gateway lost its way to the host and a dropped file's upload
// hung for minutes while the terminal held every key typed. Esc typed alone while one runs stops
// it, as the banner's Cancel does. No imports, so `node --test` runs upload.test.ts as it is.

/** Bytes are left and none went for this long: the way to the machine is down. */
export const UPLOAD_STALL_MS = 15_000;
/** Every byte went and no answer came for this long. */
export const UPLOAD_REPLY_MS = 30_000;

const DOWN = 'the way to your cloud machine may be down';

/** Like fetch's POST, for a file: a request (fetch can't) tells how many bytes have gone, and can be stopped. */
export function postFile(path: string, body: Blob, type: string, signal: AbortSignal, onProgress: (sent: number) => void): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const cancelled = () => new DOMException('Upload cancelled', 'AbortError');
    if (signal.aborted) return reject(cancelled());
    const x = new XMLHttpRequest();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onAbort = () => x.abort();
    const end = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    };
    const fail = (e: Error) => {
      end();
      reject(e);
    };
    // Each step forward starts the wait over: for the next bytes, or for the answer once all went.
    const watch = (sent: number) => {
      clearTimeout(timer);
      const all = sent >= body.size;
      const ms = all ? UPLOAD_REPLY_MS : UPLOAD_STALL_MS;
      timer = setTimeout(() => {
        fail(new Error(all ? `sent, but no answer for ${ms / 1000}s: ${DOWN}` : `nothing went through for ${ms / 1000}s: ${DOWN}`));
        x.abort();
      }, ms);
    };
    x.open('POST', path);
    x.withCredentials = true;
    x.setRequestHeader('Content-Type', type);
    x.setRequestHeader('X-Cmx-Upload', '1');
    x.upload.onprogress = (e) => {
      onProgress(e.loaded);
      watch(e.loaded);
    };
    x.upload.onload = () => watch(body.size);
    x.onload = () => {
      end();
      let j: Record<string, unknown> = {};
      try {
        j = JSON.parse(x.responseText);
      } catch {
        /* not JSON */
      }
      if (x.status >= 200 && x.status < 300) resolve(j);
      else reject(new Error(typeof j.error === 'string' ? j.error : `HTTP ${x.status}`));
    };
    x.onerror = () => fail(new Error('cmx didn’t answer · check the connection'));
    x.onabort = () => fail(cancelled());
    signal.addEventListener('abort', onAbort, { once: true });
    x.send(body);
    watch(0);
  });
}

/** What stops the upload running in each session (one at a time: drops.ts chains them). */
const running = new Map<number, () => void>();

/** A session's upload started; `stop` ends it. Call what this returns when it's over. */
export function uploadRunning(sid: number, stop: () => void): () => void {
  running.set(sid, stop);
  return () => {
    if (running.get(sid) === stop) running.delete(sid);
  };
}

/** Stop a session's running upload (the banner's Cancel): what was sent stays, no path is pasted. False when none runs. */
export function cancelUpload(sid: number): boolean {
  const stop = running.get(sid);
  if (!stop) return false;
  stop();
  return true;
}

/** Esc typed alone while a session's upload runs stops it, and isn't sent (the core's rule). */
export function escCancelsUpload(sid: number, typed: string): boolean {
  return typed === '\x1b' && cancelUpload(sid);
}
